// Base Libs
import { shallowEqual } from "react-redux";
import { GuxButton, GuxIcon, GuxTooltip } from "genesys-spark-components-react";

// Types & Enums
import { VisibilityConditionCombiningOperationEnum } from "@genesys/cloud-frontend/dist/mjs/models";
import FeatureToggle from "enums/FeatureToggle";
import FormType from "enums/FormType";

// Components
import { I18nComponent } from "react/utils/I18nComponent";
import VisibilityCondition from "./VisibilityCondition";
import ConditionCombiningOperatorDropDown from "./ConditionCombiningOperatorDropDown";

// Hooks
import { useStrings } from "features/i18n/useStrings";
import { useAppDispatch, useAppSelector } from "app/hooks";

// Redux
import { ExpandedVisibilityCondition } from "features/qualityForm/qualityFormSlices/qualityFormTypes";
import {
    qualityFormActions,
    selectEligibleQuestionsForVisibilityConditionsForQuestion,
    selectEligibleQuestionsForVisibilityConditionsForQuestionGroup,
    selectQuestionGroup,
} from "features/qualityForm/qualityFormSlice";
import {
    selectFormType,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";

// Styles & Strings
import "./VisibilityConditionContainer.less";
import strings from "./VisibilityCondition.strings";
import { toGuxDisabled } from "util/componentUtil";

export type VisibilityConditionQuestions = Array<{
    questionId: string;
    questionName: string;
    answers: Array<string>;
}>;

export default function VisibilityConditionContainer({
    existingVisibilityConditions,
    eligibleQuestionsForVisibilityConditions,
    onAddPredicate,
    onDeletePredicate,
    onUpdatePredicateQuestion,
    onUpdatePredicateAnswer,
    onUpdateConditionOperator,
}: {
    existingVisibilityConditions?: ExpandedVisibilityCondition;
    eligibleQuestionsForVisibilityConditions: Array<string>;
    onAddPredicate: () => void;
    onDeletePredicate: (predicateIndex: number) => void;
    onUpdatePredicateQuestion: (
        conditionalQuestionId: string,
        predicateIndex: number
    ) => void;
    onUpdatePredicateAnswer: (
        conditionalAnswerId: string,
        predicateIndex: number
    ) => void;
    onUpdateConditionOperator: (
        conditionalOperator: VisibilityConditionCombiningOperationEnum
    ) => void;
}) {
    const t = useStrings(strings);
    const formType = useAppSelector((state) => selectFormType(state));
    const existingVisibilityConditionAnswers = new Set(
        existingVisibilityConditions?.predicates.map(
            (condition) => condition.answerId
        ) ?? []
    );
    const canAddMoreConditions =
        eligibleQuestionsForVisibilityConditions !== undefined &&
        eligibleQuestionsForVisibilityConditions.length > 0;
    const isVoiceSurveyFeatureEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.VOICE_SURVEY);
    });
    return (
        <div className="VisibilityConditionContainer">
            {eligibleQuestionsForVisibilityConditions &&
                existingVisibilityConditions?.combiningOperation !==
                    undefined && (
                    <>
                        <div className="logical-operators">
                            <I18nComponent
                                i18nKey={
                                    isVoiceSurveyFeatureEnabled
                                        ? formType === FormType.SURVEY
                                            ? "qualityFormEditor.visibilityCondition.labels.includeTitle"
                                            : "qualityFormEditor.visibilityCondition.labels.title"
                                        : "qualityFormEditor.visibilityCondition.labels.title"
                                }
                                substitutions={{
                                    condition: (
                                        <ConditionCombiningOperatorDropDown
                                            visibilityConditionOperator={
                                                existingVisibilityConditions?.combiningOperation
                                            }
                                            onUpdate={onUpdateConditionOperator}
                                        />
                                    ),
                                }}
                            />
                        </div>
                        <div className="conditions">
                            {existingVisibilityConditions?.predicates?.map(
                                (val, index) => {
                                    return (
                                        <VisibilityCondition
                                            selectedQuestionId={
                                                val.questionId ?? ""
                                            }
                                            selectedAnswerId={
                                                val.answerId ?? ""
                                            }
                                            eligibleQuestionIds={
                                                eligibleQuestionsForVisibilityConditions
                                            }
                                            existingVisibilityConditionAnswers={
                                                existingVisibilityConditionAnswers
                                            }
                                            key={val.questionId + index}
                                            hideDelete={
                                                existingVisibilityConditions
                                                    .predicates.length === 1
                                            }
                                            onDeletePredicate={() =>
                                                onDeletePredicate(index)
                                            }
                                            onUpdatePredicateQuestion={(
                                                conditionalQuestionId: string
                                            ) =>
                                                onUpdatePredicateQuestion(
                                                    conditionalQuestionId,
                                                    index
                                                )
                                            }
                                            onUpdatePredicateAnswer={(
                                                conditionalAnswerId: string
                                            ) =>
                                                onUpdatePredicateAnswer(
                                                    conditionalAnswerId,
                                                    index
                                                )
                                            }
                                        />
                                    );
                                }
                            )}
                        </div>
                    </>
                )}
            <div className="add-condition">
                <GuxButton
                    onClick={() => onAddPredicate()}
                    disabled={toGuxDisabled(!canAddMoreConditions)}
                    aria-disabled={toGuxDisabled(!canAddMoreConditions)}
                >
                    <GuxIcon icon-name="add" decorative />
                    <span>{t.labels.addVisibilityCondition}</span>
                    {!canAddMoreConditions && (
                        <GuxTooltip>{t.errors.noConditionLeft}</GuxTooltip>
                    )}
                </GuxButton>
            </div>
        </div>
    );
}

export function QuestionVisibilityConditions({
    questionId,
}: {
    questionId: string;
}) {
    const dispatch = useAppDispatch();
    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });
    const existingVisibilityConditions = useAppSelector((state) => {
        return selectQuestion(state, questionId)?.visibilityCondition;
    }, shallowEqual);

    const eligibleQuestionsForVisibilityConditions = useAppSelector(
        (state) =>
            selectEligibleQuestionsForVisibilityConditionsForQuestion(
                state,
                questionId,
                false
            ) ?? [],
        shallowEqual
    );

    return (
        <>
            {eligibleQuestionsForVisibilityConditions && (
                <VisibilityConditionContainer
                    existingVisibilityConditions={existingVisibilityConditions}
                    eligibleQuestionsForVisibilityConditions={
                        eligibleQuestionsForVisibilityConditions
                    }
                    onAddPredicate={() => {
                        dispatch(
                            qualityFormActions.addVisibilityPredicate({
                                id: questionId,
                                formEntity: "question",
                                defaultQuestionId:
                                    eligibleQuestionsForVisibilityConditions[0],
                                isMultipleSelectFTEnabled,
                            })
                        );
                    }}
                    onDeletePredicate={(predicateIndex: number) => {
                        dispatch(
                            qualityFormActions.deleteVisibilityPredicate({
                                id: questionId,
                                formEntity: "question",
                                predicateIndex,
                            })
                        );
                    }}
                    onUpdatePredicateQuestion={(
                        conditionalQuestionId: string,
                        predicateIndex: number
                    ) => {
                        dispatch(
                            qualityFormActions.updateVisibilityPredicateQuestion(
                                {
                                    conditionalQuestionId,
                                    predicateIndex,
                                    id: questionId,
                                    formEntity: "question",
                                    isMultipleSelectFTEnabled,
                                }
                            )
                        );
                    }}
                    onUpdatePredicateAnswer={(
                        conditionalAnswerId: string,
                        predicateIndex: number
                    ) => {
                        dispatch(
                            qualityFormActions.updateVisibilityPredicateAnswer({
                                id: questionId,
                                formEntity: "question",
                                conditionalAnswerId,
                                predicateIndex,
                            })
                        );
                    }}
                    onUpdateConditionOperator={(
                        conditionalOperator: VisibilityConditionCombiningOperationEnum
                    ) => {
                        dispatch(
                            qualityFormActions.changeVisibilityConditionCombiningOperator(
                                {
                                    id: questionId,
                                    formEntity: "question",
                                    conditionalOperator,
                                }
                            )
                        );
                    }}
                />
            )}
        </>
    );
}

export function QuestionGroupVisibilityConditions({
    questionGroupId,
}: {
    questionGroupId: string;
}) {
    const dispatch = useAppDispatch();
    const isMultipleSelectFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT);
    });

    const existingVisibilityConditions = useAppSelector((state) => {
        return selectQuestionGroup(state, questionGroupId)?.visibilityCondition;
    }, shallowEqual);

    const eligibleQuestionsForVisibilityConditions = useAppSelector(
        (state) =>
            selectEligibleQuestionsForVisibilityConditionsForQuestionGroup(
                state,
                questionGroupId,
                false
            ) ?? [],
        shallowEqual
    );

    return (
        <>
            {eligibleQuestionsForVisibilityConditions && (
                <VisibilityConditionContainer
                    existingVisibilityConditions={existingVisibilityConditions}
                    eligibleQuestionsForVisibilityConditions={
                        eligibleQuestionsForVisibilityConditions
                    }
                    onAddPredicate={() => {
                        dispatch(
                            qualityFormActions.addVisibilityPredicate({
                                id: questionGroupId,
                                formEntity: "questionGroup",
                                defaultQuestionId:
                                    eligibleQuestionsForVisibilityConditions[0],
                                isMultipleSelectFTEnabled,
                            })
                        );
                    }}
                    onDeletePredicate={(predicateIndex: number) => {
                        dispatch(
                            qualityFormActions.deleteVisibilityPredicate({
                                id: questionGroupId,
                                formEntity: "questionGroup",
                                predicateIndex,
                            })
                        );
                    }}
                    onUpdatePredicateQuestion={(
                        conditionalQuestionId: string,
                        predicateIndex: number
                    ) => {
                        dispatch(
                            qualityFormActions.updateVisibilityPredicateQuestion(
                                {
                                    conditionalQuestionId,
                                    predicateIndex,
                                    id: questionGroupId,
                                    formEntity: "questionGroup",
                                    isMultipleSelectFTEnabled,
                                }
                            )
                        );
                    }}
                    onUpdatePredicateAnswer={(
                        conditionalAnswerId: string,
                        predicateIndex: number
                    ) => {
                        dispatch(
                            qualityFormActions.updateVisibilityPredicateAnswer({
                                id: questionGroupId,
                                formEntity: "questionGroup",
                                conditionalAnswerId,
                                predicateIndex,
                            })
                        );
                    }}
                    onUpdateConditionOperator={(
                        conditionalOperator: VisibilityConditionCombiningOperationEnum
                    ) => {
                        dispatch(
                            qualityFormActions.changeVisibilityConditionCombiningOperator(
                                {
                                    id: questionGroupId,
                                    formEntity: "questionGroup",
                                    conditionalOperator,
                                }
                            )
                        );
                    }}
                />
            )}
        </>
    );
}
